import React, { createContext, useContext, useReducer, ReactNode, useEffect } from 'react';
import {
  AuthState,
  AuthContextType,
  LoginCredentials,
  RegisterData,
  ParentRegisterData,
  TeacherRegisterData,
  User
} from '../types/auth';
import { loginUser, registerUser, registerParent as registerParentUser, registerTeacher as registerTeacherUser, getCurrentUser, logoutUser } from '../services/authService';
import { isTokenValid } from '../utils/jwtUtils';

// Initial state
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  loading: true,
  error: null
};

// Action types
enum ActionType {
  AUTH_START = 'AUTH_START',
  AUTH_SUCCESS = 'AUTH_SUCCESS',
  AUTH_FAILURE = 'AUTH_FAILURE',
  USER_LOADED = 'USER_LOADED',
  LOGOUT = 'LOGOUT',
  CLEAR_ERROR = 'CLEAR_ERROR'
}

// Action interfaces
interface AuthStartAction {
  type: ActionType.AUTH_START;
}

interface AuthSuccessAction {
  type: ActionType.AUTH_SUCCESS;
  payload: { user: User };
}

interface AuthFailureAction {
  type: ActionType.AUTH_FAILURE;
  payload: string | null;
}

interface UserLoadedAction {
  type: ActionType.USER_LOADED;
  payload: User;
}

interface LogoutAction {
  type: ActionType.LOGOUT;
}

interface ClearErrorAction {
  type: ActionType.CLEAR_ERROR;
}

type AuthAction = 
  | AuthStartAction
  | AuthSuccessAction
  | AuthFailureAction
  | UserLoadedAction
  | LogoutAction
  | ClearErrorAction;

// Reducer function
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case ActionType.AUTH_START:
      return {
        ...state,
        loading: true,
        error: null
      };
    case ActionType.AUTH_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        loading: false,
        error: null
      };
    case ActionType.AUTH_FAILURE:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        loading: false,
        error: action.payload
      };
    case ActionType.USER_LOADED:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        loading: false
      };
    case ActionType.LOGOUT:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        loading: false,
        error: null
      };
    case ActionType.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
    default:
      return state;
  }
};

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [authState, dispatch] = useReducer(authReducer, initialState);

  // Load user on mount if we have a valid token
  useEffect(() => {
    const loadUser = async () => {
      dispatch({ type: ActionType.AUTH_START });
      
      // Only try to load user if we have a valid token
      if (isTokenValid()) {
        try {
          const user = await getCurrentUser();
          dispatch({ type: ActionType.USER_LOADED, payload: user });
        } catch (error) {
          console.error('Failed to load user:', error);
          dispatch({ 
            type: ActionType.AUTH_FAILURE, 
            payload: null // Don't display any error message to the user
          });
        }
      } else {
        // No valid token, so we're not authenticated
        dispatch({
          type: ActionType.AUTH_FAILURE,
          payload: null
        });
      }
    };

    loadUser();
  }, []);

  // Login function
  const login = async (credentials: LoginCredentials) => {
    dispatch({ type: ActionType.AUTH_START });
    
    try {
      const { user } = await loginUser(credentials);
      
      dispatch({
        type: ActionType.AUTH_SUCCESS,
        payload: { user }
      });
    } catch (error) {
      let errorMessage = 'Failed to login';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      dispatch({
        type: ActionType.AUTH_FAILURE,
        payload: errorMessage
      });
    }
  };

  // Register function
  const register = async (data: RegisterData) => {
    dispatch({ type: ActionType.AUTH_START });

    try {
      const { user } = await registerUser(data);

      dispatch({
        type: ActionType.AUTH_SUCCESS,
        payload: { user }
      });
    } catch (error) {
      let errorMessage = 'Failed to register';
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      dispatch({
        type: ActionType.AUTH_FAILURE,
        payload: errorMessage
      });
    }
  };

  // Register parent function
  const registerParent = async (data: ParentRegisterData) => {
    dispatch({ type: ActionType.AUTH_START });

    try {
      const { user } = await registerParentUser(data);

      dispatch({
        type: ActionType.AUTH_SUCCESS,
        payload: { user }
      });
    } catch (error) {
      let errorMessage = 'Failed to register parent account';
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      dispatch({
        type: ActionType.AUTH_FAILURE,
        payload: errorMessage
      });
    }
  };

  // Register teacher function
  const registerTeacher = async (data: TeacherRegisterData) => {
    dispatch({ type: ActionType.AUTH_START });

    try {
      const { teacher } = await registerTeacherUser(data);

      dispatch({
        type: ActionType.AUTH_SUCCESS,
        payload: { user: teacher }
      });
    } catch (error) {
      let errorMessage = 'Failed to register teacher account';
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      dispatch({
        type: ActionType.AUTH_FAILURE,
        payload: errorMessage
      });
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await logoutUser(); // Clear the cookie on the server
    } catch (error) {
      console.error('Error during logout:', error);
      // Continue with local logout even if server logout fails
    }
    
    dispatch({ type: ActionType.LOGOUT });
  };

  // Clear error function
  const clearAuthError = () => {
    dispatch({ type: ActionType.CLEAR_ERROR });
  };

  // Update user profile function
  const updateCurrentUser = (updatedUser: User) => {
    dispatch({ 
      type: ActionType.USER_LOADED, 
      payload: updatedUser 
    });
  };

  return (
    <AuthContext.Provider
      value={{
        authState,
        login,
        register,
        registerParent,
        registerTeacher,
        logout,
        clearAuthError,
        updateCurrentUser
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook for using the auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};